// mirror of auware/.../frontend/src/pages/auth/ResetPasswordPage.tsx (see frontend/CLAUDE.md mirror policy).
//
// Buyist adaptations: brand strings + logo swapped.
import { type FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import Button from "@/components/ui/Button";
import { usePageTitle } from "@/hooks/usePageTitle";
import api from "@/lib/api";

// ---------------------------------------------------------------------------
// Password strength helpers
// ---------------------------------------------------------------------------

interface PasswordCheck {
  label: string;
  met: boolean;
}

function getPasswordChecks(password: string): PasswordCheck[] {
  return [
    { label: "At least 12 characters", met: password.length >= 12 },
    { label: "Has uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Has lowercase letter", met: /[a-z]/.test(password) },
    { label: "Has a digit", met: /\d/.test(password) },
  ];
}

/**
 * New password entry after clicking the reset link from email.
 * Reads `token` from URL query params.
 */
export function ResetPasswordPage(): JSX.Element {
  usePageTitle("Reset password");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordChecks = useMemo(() => getPasswordChecks(password), [password]);

  if (!token) {
    return (
      <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
        <div className="w-full max-w-md space-y-8 text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-neutral-600">
              Invalid reset link. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="block text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Request new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (err: unknown) {
      const message = (err as {
        response?: { data?: { detail?: string } };
      })?.response?.data?.detail ?? "Reset failed. The link may have expired.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
        <div className="w-full max-w-md space-y-8 text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
            <div className="rounded-md bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              Password reset successfully. Redirecting to sign in...
            </div>
            <Link
              to="/login"
              className="block text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Sign in now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/buyist-logo.png"
            alt="Buyist Support"
            className="mx-auto h-14 w-auto"
          />
          <h2 className="mt-2 text-xl font-semibold text-neutral-900">
            Choose a new password
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            Enter your new password below.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-6 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-neutral-700"
            >
              New password
            </label>
            <input
              id="new-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Min. 12 characters"
              autoFocus
            />
            {password.length > 0 && (
              <ul className="mt-2 space-y-1">
                {passwordChecks.map((check) => (
                  <li
                    key={check.label}
                    className={`flex items-center gap-1.5 text-xs ${
                      check.met ? "text-emerald-600" : "text-neutral-400"
                    }`}
                  >
                    <span
                      className="inline-block h-1 w-4 rounded-full"
                      style={{
                        backgroundColor: check.met ? "#16a34a" : "#d4d4d4",
                      }}
                    />
                    {check.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-neutral-700"
            >
              Confirm password
            </label>
            <input
              id="confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Re-enter your password"
            />
          </div>

          <Button type="submit" className="w-full" isLoading={loading}>
            Reset password
          </Button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
